import {
  canWorkspace as hasWorkspacePermission,
  Permissions,
  type WorkspacePermission,
} from "utils/permissions/permissionConstants";
import { resolveWorkspacePath } from "utils/routing/workspaceRouteUtils";
import type {
  WorkspaceSettingsCategory,
  WorkspaceSettingsSection,
} from "./WorkspaceSettings.sections";
import {
  WORKSPACE_SETTINGS_CATEGORIES,
  WORKSPACE_SETTINGS_SECTIONS,
} from "./WorkspaceSettings.sections";

const canManageWorkspaceMembers = (canWorkspace: (permission: WorkspacePermission) => boolean) =>
  canWorkspace(Permissions.WORKSPACE_MEMBER_LIST) ||
  canWorkspace(Permissions.WORKSPACE_MEMBER_INVITE) ||
  canWorkspace(Permissions.WORKSPACE_MEMBER_UPDATE) ||
  canWorkspace(Permissions.WORKSPACE_MEMBER_REMOVE);

export const isWorkspaceSettingsSectionVisible = (
  sectionId: string,
  canWorkspace: (permission: WorkspacePermission) => boolean
): boolean => {
  switch (sectionId) {
    case "general":
      return canWorkspace(Permissions.WORKSPACE_READ);
    case "account":
      return canWorkspace(Permissions.WORKSPACE_UPDATE);
    case "members":
      return canManageWorkspaceMembers(canWorkspace);
    case "projects":
      return canWorkspace(Permissions.WORKSPACE_PROJECT_LIST);
    case "billing":
      return (
        canWorkspace(Permissions.WORKSPACE_BILLING_READ) ||
        canWorkspace(Permissions.WORKSPACE_BILLING_UPDATE)
      );
    case "api-keys":
      return canWorkspace(Permissions.WORKSPACE_API_KEY_LIST);
    case "sso":
      return canWorkspace(Permissions.WORKSPACE_SSO_MANAGE);
    case "activity":
      return canWorkspace(Permissions.WORKSPACE_MEMBER_LIST);
    default:
      return false;
  }
};

export const filterVisibleWorkspaceSettingsSections = (
  canWorkspace: (permission: WorkspacePermission) => boolean
): WorkspaceSettingsSection[] =>
  WORKSPACE_SETTINGS_SECTIONS.filter((section) =>
    isWorkspaceSettingsSectionVisible(section.id, canWorkspace)
  );

export const getVisibleWorkspaceSettingsCategories = (
  canWorkspace: (permission: WorkspacePermission) => boolean
): WorkspaceSettingsCategory[] => {
  const visibleSectionIds = new Set(
    filterVisibleWorkspaceSettingsSections(canWorkspace).map((section) => section.id)
  );

  return WORKSPACE_SETTINGS_CATEGORIES.map((category) => ({
    ...category,
    sections: category.sections.filter((section) => visibleSectionIds.has(section.id)),
  })).filter((category) => category.sections.length > 0);
};

export const getWorkspaceSettingsSectionHref = (
  section: WorkspaceSettingsSection,
  workspaceId: string
): string => {
  if (section.id === "general") {
    return resolveWorkspacePath("/workspaces/:workspaceId/settings/general", workspaceId);
  }
  if (section.id === "members") {
    return resolveWorkspacePath("/workspaces/:workspaceId/settings/members/users", workspaceId);
  }
  return resolveWorkspacePath(section.path, workspaceId);
};

export const canViewWorkspaceSettingsSectionForRole = (
  sectionId: string,
  rolePermissions: string[]
): boolean =>
  isWorkspaceSettingsSectionVisible(sectionId, (permission) =>
    hasWorkspacePermission(permission, rolePermissions)
  );
